if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.sliderComponent = this.querySelector('slider-component');
      if (this.sliderComponent) {
        this.sliderComponent.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.enableSliderLoop();
      }
    }

    enableSliderLoop() {
      // The clone-based loop/swipe behavior only makes sense for the mobile
      // horizontal slider (.slider--mobile, <=749px). On desktop the modal
      // lays media out as a vertical stack, so cloning slides here would
      // just insert extra, visible duplicate images into that stack.
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      const slider = this.sliderComponent.slider;
      const prevButton = this.sliderComponent.querySelector('.slider-button--prev');
      const nextButton = this.sliderComponent.querySelector('.slider-button--next');
      if (!slider || !prevButton || !nextButton) return;

      const realSlides = Array.from(slider.querySelectorAll('.product-media-modal__slide')).filter(
        (slide) => getComputedStyle(slide).display !== 'none'
      );
      if (realSlides.length < 2) return;

      const stripIdentity = (clone) => {
        clone.setAttribute('data-loop-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.removeAttribute('id');
        clone.removeAttribute('data-media-id');
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.querySelectorAll('[data-media-id]').forEach((el) => el.removeAttribute('data-media-id'));
        return clone;
      };

      // Buffer clones of the first/last real slide so swiping past either end
      // lands on real-looking content instead of just stopping dead.
      const firstClone = stripIdentity(realSlides[0].cloneNode(true));
      const lastClone = stripIdentity(realSlides[realSlides.length - 1].cloneNode(true));
      slider.insertBefore(lastClone, realSlides[0]);
      slider.appendChild(firstClone);

      const total = realSlides.length;
      const multiplier = typeof isRtl !== 'undefined' && isRtl ? -1 : 1;
      const slideWidth = () => realSlides[0].offsetWidth;
      const scrollToIndex = (index, behavior) => {
        const width = slideWidth();
        if (!width) return;
        slider.scrollTo({ left: index * width * multiplier, behavior });
      };
      const rawIndex = () => {
        const width = slideWidth();
        return width ? Math.round((slider.scrollLeft * multiplier) / width) : 1;
      };

      const counterCurrent = this.querySelector('.slider-counter--current');

      // The built-in dot links assume no buffer clones, so their click handler would
      // land one slide off. Replace them with links that use our clone-aware index.
      const dots = Array.from(this.sliderComponent.querySelectorAll('.slider-counter__link')).map((dot, dotIndex) => {
        const newDot = dot.cloneNode(true);
        dot.replaceWith(newDot);
        newDot.addEventListener('click', (event) => {
          event.preventDefault();
          scrollToIndex(dotIndex + 1, 'smooth');
        });
        return newDot;
      });

      // Cap how many dots are ever shown at once; beyond that, keep a window of
      // dots centered on the active one and fade the ones at its edges (like
      // iOS's truncated page control) instead of rendering every single dot.
      const MAX_VISIBLE_DOTS = 10;
      const dotsTotal = dots.length;
      const updateDotsWindow = (activeIndex) => {
        dots.forEach((dot) => {
          dot.classList.remove(
            'slider-counter__link--hidden',
            'slider-counter__link--edge-1',
            'slider-counter__link--edge-2'
          );
        });
        if (dotsTotal <= MAX_VISIBLE_DOTS) return;

        const half = Math.floor(MAX_VISIBLE_DOTS / 2);
        let start = activeIndex - half;
        let end = start + MAX_VISIBLE_DOTS - 1;
        if (start < 0) {
          start = 0;
          end = MAX_VISIBLE_DOTS - 1;
        } else if (end > dotsTotal - 1) {
          end = dotsTotal - 1;
          start = end - MAX_VISIBLE_DOTS + 1;
        }

        dots.forEach((dot, dotIndex) => {
          if (dotIndex < start || dotIndex > end) {
            dot.classList.add('slider-counter__link--hidden');
            return;
          }
          if (dotIndex === activeIndex) return;
          const edgeDistance = Math.min(dotIndex - start, end - dotIndex);
          if (edgeDistance === 0) {
            dot.classList.add('slider-counter__link--edge-2');
          } else if (edgeDistance === 1) {
            dot.classList.add('slider-counter__link--edge-1');
          }
        });
      };
      updateDotsWindow(0);

      // Real slide indexes are offset by 1 in scroll-space because of the prepended clone.
      let correcting = false;
      let settleTimeout;
      slider.addEventListener('scroll', () => {
        if (correcting) return;
        const index = rawIndex();
        const realIndex = ((index - 1) % total + total) % total;
        if (counterCurrent) {
          counterCurrent.textContent = realIndex + 1;
        }
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('slider-counter__link--active', dotIndex === realIndex);
          if (dotIndex === realIndex) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
        updateDotsWindow(realIndex);

        clearTimeout(settleTimeout);
        settleTimeout = setTimeout(() => {
          // "instant" ignores the theme's global scroll-behavior:smooth so this
          // correction is an invisible jump rather than a noticeable rewind.
          if (index === 0) {
            correcting = true;
            scrollToIndex(total, 'instant');
            correcting = false;
          } else if (index === total + 1) {
            correcting = true;
            scrollToIndex(1, 'instant');
            correcting = false;
          }
        }, 120);
      });

      // Replace the buttons to drop the slider-component's own click listener,
      // which stops (and disables the button) at the first/last slide instead of wrapping.
      const newPrevButton = prevButton.cloneNode(true);
      const newNextButton = nextButton.cloneNode(true);
      // The clone may have inherited a "disabled" attribute from the boundary state
      // at clone time (e.g. prev is disabled while sitting on slide 1) - looping means
      // neither button should ever be disabled.
      newPrevButton.removeAttribute('disabled');
      newNextButton.removeAttribute('disabled');
      prevButton.replaceWith(newPrevButton);
      nextButton.replaceWith(newNextButton);

      const goToSlide = (step) => scrollToIndex(rawIndex() + step, 'smooth');

      newPrevButton.addEventListener('click', (event) => {
        event.preventDefault();
        goToSlide(-1);
      });
      newNextButton.addEventListener('click', (event) => {
        event.preventDefault();
        goToSlide(1);
      });
    }

    hide() {
      super.hide();
    }

    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }

    showActiveMedia() {
      const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`);
      if (!activeMedia) return;

      this.setActiveMedia(activeMedia);

      const activeSlide = activeMedia.closest('.product-media-modal__slide');
      if (activeSlide && this.sliderComponent && this.sliderComponent.slider) {
        this.sliderComponent.slider.scrollTo({ left: activeSlide.offsetLeft, behavior: 'auto' });
      }

      activeMedia.scrollIntoView({ block: 'nearest' });
    }

    onSlideChanged(event) {
      const currentElement = event.detail && event.detail.currentElement;
      if (!currentElement) return;

      const activeMedia = currentElement.matches('[data-media-id]')
        ? currentElement
        : currentElement.querySelector('[data-media-id]');
      if (!activeMedia) return;

      this.setActiveMedia(activeMedia);
    }

    setActiveMedia(activeMedia) {
      this.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('active');
      });
      activeMedia.classList.add('active');

      this.playActiveMedia(activeMedia);
    }

    playActiveMedia(activeMedia) {
      window.pauseAllMedia();

      const deferredMedia = activeMedia.classList.contains('deferred-media')
        ? activeMedia
        : activeMedia.querySelector('.deferred-media');
      if (!deferredMedia) return;

      const activeMediaTemplate = deferredMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

      if (deferredMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
        deferredMedia.loadContent();
    }
  });
}
